<script setup>
/**
 * CartDepositRow Component
 * Read-only deposit summary card displayed in cart when deposit > 0
 */
import { computed } from 'vue'
import { formatPrice } from '@core/utils/formatting.js'

const props = defineProps({
  depositPreVat: {
    type: Number,
    required: true,
  },
  depositUnits: {
    type: Number,
    default: 0,
  },
})

// Format units (round to reasonable precision)
const formattedUnits = computed(() => {
  if (!props.depositUnits) return null
  return Number.isInteger(props.depositUnits) ? props.depositUnits : props.depositUnits.toFixed(1)
})
</script>

<template>
  <div class="deposit-row">
    <!-- Icon (Bottle) -->
    <div class="deposit-icon">
      <svg
        viewBox="0 0 24 24"
        width="20"
        height="20"
        fill="none"
        stroke="currentColor"
        stroke-width="2"
      >
        <path d="M8 2h8v4H8z" />
        <path d="M9 6v2a4 4 0 00-2 3.5V20a2 2 0 002 2h6a2 2 0 002-2v-8.5A4 4 0 0015 8V6" />
        <path d="M7 14h10" />
      </svg>
    </div>

    <!-- Details -->
    <div class="deposit-details">
      <div class="deposit-title">פיקדון</div>
      <div class="deposit-subtitle">מצטבר מפריטים עם פיקדון</div>
      <div v-if="formattedUnits" class="deposit-units">יח' פיקדון: {{ formattedUnits }}</div>
    </div>

    <!-- Total -->
    <div class="deposit-total">
      <span class="total-value">{{ formatPrice(depositPreVat) }}</span>
      <span class="total-currency">₪</span>
    </div>
  </div>
</template>

<style scoped>
.deposit-row {
  display: flex;
  align-items: center;
  gap: var(--spacing-sm);
  padding: var(--spacing-sm) var(--spacing-md);
  background: rgba(139, 92, 246, 0.08);
  border: 1px solid rgba(139, 92, 246, 0.25);
  border-radius: var(--border-radius);
}

.deposit-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  flex-shrink: 0;
  background: rgba(139, 92, 246, 0.15);
  border-radius: var(--border-radius-sm);
  color: #7c3aed;
}

.deposit-details {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: var(--spacing-sm);
}

.deposit-title {
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-semibold);
  color: #7c3aed;
}

.deposit-subtitle {
  font-size: var(--font-size-xs);
  color: var(--text-muted);
}

.deposit-units {
  font-size: var(--font-size-xs);
  color: var(--text-secondary);
}

.deposit-total {
  display: flex;
  align-items: baseline;
  gap: 2px;
  flex-shrink: 0;
}

.total-value {
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-bold);
  color: #7c3aed;
}

.total-currency {
  font-size: var(--font-size-xs);
  color: #7c3aed;
}

/* Mobile */
@media (max-width: 767px) {
  .deposit-row {
    padding: var(--spacing-xs) var(--spacing-sm);
  }

  .deposit-icon {
    width: 28px;
    height: 28px;
  }

  .deposit-icon svg {
    width: 16px;
    height: 16px;
  }
}
</style>
